import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { compendiumPath, type BookEntry } from "@/config/site";
import type { CompendiumKind } from "@/data/compendium";
import { COMPENDIUM_SECTIONS } from "@/data/compendium-sections";
import { loadCompendium } from "@/data/load-compendium";
import { loadPortraitUrls } from "@/data/load-book-home";
import { useScrollToHash } from "./useScrollToHash";

interface Row {
  id: string;
  name: string;
  kind: CompendiumKind;
  href: string;
  imageUrl: string | undefined;
}

async function rowsFor(book: BookEntry): Promise<Row[]> {
  const entries = await loadCompendium(book.id);
  const urlOf = await loadPortraitUrls(book.id);
  return entries.map((entry) => ({
    id: entry.id,
    name: entry.name,
    kind: entry.kind,
    href: compendiumPath(book, entry.id, entry.kind),
    imageUrl: urlOf(entry.imageStem),
  }));
}

/** Key items keep their own fuller list, so they are not repeated here. */
const SECTIONS = COMPENDIUM_SECTIONS.filter((section) => section.kind !== "item");

/** A series' compendium, a list for each kind. Each list hides when it has none. */
export function CompendiumEntries({ books }: { books: readonly BookEntry[] }) {
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    void Promise.all(books.map(rowsFor)).then((all) => {
      const seen = new Set<string>();
      setRows(all.flat().filter((row) => {
        const key = `${row.kind}:${row.id}`;
        return !seen.has(key) && seen.add(key);
      }));
    });
  }, [books]);

  const seriesId = books[0]?.seriesId ?? "";
  return SECTIONS.map(({ kind, folder, title }) => (
    <EntryList
      key={kind}
      id={folder}
      title={title(seriesId)}
      rows={rows.filter((row) => row.kind === kind)}
    />
  ));
}

function EntryList({ id, title, rows }: { id: string; title: string; rows: readonly Row[] }) {
  useScrollToHash(id, rows.length > 0);
  if (rows.length === 0) {
    return null;
  }
  return (
    <section id={id} className="race-entries" aria-label={title}>
      <h2 className="book-section-title">{title}</h2>
      <ul className="race-entries__list">
        {rows.map((row) => (
          <li key={row.id}>
            <Link className="race-entries__race" to={row.href}>
              {row.imageUrl === undefined
                ? <span className="race-entries__image" aria-hidden="true" />
                : <img className="race-entries__image" src={row.imageUrl} alt="" />}
              {row.name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
